import { useEffect, useRef, useState } from 'react';
import {
  PRAZNO_KUCANSTVO,
  podaciZaObrazac,
  provjeriKucanstvo,
  pronadjiDuplikat,
  slicnaKucanstva,
} from '../lib/unosKucanstva';
import { uOkviru } from '../lib/konstante';
import PoljaKucanstva from './PoljaKucanstva';
import Ikona from './Ikona';
import { spremiUzPrivole } from '../lib/dokumenti';
import OdabirDokumenata from './OdabirDokumenata';

function ucitajNacrt(kljuc) {
  try {
    const spremljeno = JSON.parse(sessionStorage.getItem(kljuc));
    if (
      spremljeno?.zahtjev &&
      spremljeno.podaci &&
      Array.isArray(spremljeno.kontakti)
    )
      return {
        ...spremljeno,
        podaci: { ...PRAZNO_KUCANSTVO, ...spremljeno.podaci },
      };
  } catch {
    /* Neispravan nacrt ne smije spriječiti otvaranje obrasca. */
  }
  return {
    podaci: { ...PRAZNO_KUCANSTVO },
    kontakti: [],
    zahtjev: crypto.randomUUID(),
  };
}

export default function UnosKucanstva({
  korisnikId,
  jeUprava,
  urediId,
  kucanstva,
  ucitavaEvidenciju,
  naDohvat,
  naSpremi,
  naPrivole,
  naSpremljeno,
  naOdustani,
  naPostojece,
}) {
  const kljuc = `vodovod-nacrt:${korisnikId}`;
  const [nacrt, postaviNacrt] = useState(() =>
    urediId
      ? {
          podaci: { ...PRAZNO_KUCANSTVO },
          kontakti: [],
          zahtjev: crypto.randomUUID(),
        }
      : ucitajNacrt(kljuc),
  );
  const [izvorni, postaviIzvorni] = useState(null);
  const [ucitava, postaviUcitava] = useState(!!urediId);
  const [radi, postaviRadi] = useState(false);
  const [privole, postaviPrivole] = useState([]);
  const [pripremaPrivole, postaviPripremaPrivole] = useState(false);
  const [napredakPrivole, postaviNapredakPrivole] = useState('');
  const spremljeni = nacrt.spremljeni ?? null;
  const [greske, postaviGreske] = useState([]);
  const [izvanPotvrdjeno, postaviIzvanPotvrdjeno] = useState(false);
  const [slicnoPotvrdjeno, postaviSlicnoPotvrdjeno] = useState(false);
  const [imaVezu, postaviImaVezu] = useState(navigator.onLine);
  const zakljucano = useRef(false);
  const sazetakGresaka = useRef(null);
  useEffect(() => {
    if (greske.length) {
      sazetakGresaka.current?.focus();
      sazetakGresaka.current?.scrollIntoView({ block: 'center' });
    }
  }, [greske]);
  const { podaci, kontakti } = nacrt;

  useEffect(() => {
    if (!urediId) return;
    let aktivno = true;
    naDohvat(urediId)
      .then(({ zapis, kontakti }) => {
        if (!aktivno) return;
        postaviIzvorni(zapis);
        postaviNacrt({
          podaci: podaciZaObrazac(zapis),
          kontakti,
          zahtjev: crypto.randomUUID(),
        });
      })
      .catch((e) => {
        if (aktivno) postaviGreske([e.message]);
      })
      .finally(() => {
        if (aktivno) postaviUcitava(false);
      });
    return () => {
      aktivno = false;
    };
  }, [urediId, naDohvat, jeUprava, korisnikId]);
  useEffect(() => {
    if (urediId) return;
    try {
      sessionStorage.setItem(kljuc, JSON.stringify(nacrt));
    } catch {
      /* Obrazac ostaje dostupan u memoriji kartice. */
    }
  }, [nacrt, kljuc, urediId]);
  useEffect(() => {
    postaviIzvanPotvrdjeno(false);
    postaviSlicnoPotvrdjeno(false);
  }, [podaci.naselje, podaci.kucni_broj, podaci.lat, podaci.lng]);
  useEffect(() => {
    const provjeri = () => postaviImaVezu(navigator.onLine);
    window.addEventListener('online', provjeri);
    window.addEventListener('offline', provjeri);
    return () => {
      window.removeEventListener('online', provjeri);
      window.removeEventListener('offline', provjeri);
    };
  }, []);

  const duplikat = pronadjiDuplikat(podaci, kucanstva, urediId);
  const slicni = !urediId
    ? slicnaKucanstva(podaci, kucanstva).filter((k) => k.id !== duplikat?.id)
    : [];
  const izvan =
    podaci.lat &&
    podaci.lng &&
    !uOkviru(
      Number(String(podaci.lat).replace(',', '.')),
      Number(String(podaci.lng).replace(',', '.')),
    );
  const spremi = async (e) => {
    e.preventDefault();
    if (
      zakljucano.current ||
      pripremaPrivole ||
      (ucitavaEvidenciju && !spremljeni)
    )
      return;
    const poruke = spremljeni ? [] : provjeriKucanstvo(podaci, kontakti);
    if (!spremljeni && duplikat)
      poruke.push('Ova adresa već postoji. Otvori postojeće kućanstvo.');
    if (!spremljeni && slicni.length && !slicnoPotvrdjeno)
      poruke.push(
        'Pregledaj slične zapise i potvrdi da unosiš zasebno kućanstvo.',
      );
    if (!spremljeni && izvan && !izvanPotvrdjeno)
      poruke.push('Provjeri i potvrdi koordinate izvan područja vodovoda.');
    if (
      nacrt.datumPrivole &&
      nacrt.datumPrivole >
        new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Zagreb' }).format(
          new Date(),
        )
    )
      poruke.push('Datum potpisivanja ne može biti u budućnosti.');
    if (spremljeni && nacrt.privolaNaCekanju && !privole.length)
      poruke.push(
        'Kućanstvo je već spremljeno. Ponovno odaberi privolu ili dovrši unos bez preostalih priloga.',
      );
    if (poruke.length) {
      postaviGreske(poruke);
      return;
    }
    zakljucano.current = true;
    postaviRadi(true);
    postaviGreske([]);
    try {
      const rezultat = await spremiUzPrivole({
        spremljeni,
        naSpremi: () =>
          naSpremi({
            ...nacrt,
            id: urediId,
            ocekivano: izvorni?.updated_at ?? null,
          }),
        privole,
        datumPrivole: nacrt.datumPrivole,
        naPrivole,
        naNapredak: postaviNapredakPrivole,
        naEvidentirano: (rezultat) => {
          const spremljeniNacrt = {
            ...nacrt,
            spremljeni: rezultat,
            privolaNaCekanju: !!privole.length,
          };
          postaviNacrt(spremljeniNacrt);
          if (!urediId) {
            try {
              sessionStorage.setItem(kljuc, JSON.stringify(spremljeniNacrt));
            } catch {
              /* Ostaje u memoriji. */
            }
          }
        },
      });
      if (!urediId) {
        try {
          sessionStorage.removeItem(kljuc);
        } catch {
          /* Nacrt nije dostupan. */
        }
      }
      naSpremljeno(rezultat);
    } catch (e) {
      postaviGreske([
        e.message,
        'Ako je kućanstvo već spremljeno, ponovni pokušaj šalje samo priloge i ne stvara novo kućanstvo.',
      ]);
    } finally {
      zakljucano.current = false;
      postaviRadi(false);
      postaviNapredakPrivole('');
    }
  };
  if (ucitava)
    return (
      <div className="cekanje">
        <span className="ucitavanje" />
        <p>Učitavam kućanstvo i kontakte…</p>
      </div>
    );
  if (urediId && !izvorni)
    return (
      <div className="obavijest greska" role="alert">
        {greske.join(' ')}
        <button className="tekst-gumb" onClick={naOdustani}>
          Vrati se na popis
        </button>
      </div>
    );
  return (
    <form className="unos-kucanstva" onSubmit={spremi} noValidate>
      <div className="uvod-unosa">
        <Ikona naziv="info" velicina={19} />
        <p>
          {urediId
            ? 'Izmjene kućanstva i njegovih kontakata spremaju se zajedno.'
            : 'Unosiš novo kućanstvo u novu evidenciju. Stara bilježnica ostaje zasebna.'}
        </p>
        {!urediId && <span>Nacrt do odjave ili zatvaranja kartice</span>}
      </div>
      {greske.length > 0 && (
        <div
          className="obavijest greska"
          role="alert"
          ref={sazetakGresaka}
          tabIndex={-1}
        >
          <strong>Provjeri unos</strong>
          <ul>
            {greske.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
      )}
      {spremljeni && (
        <p className="obavijest ok" role="status">
          Kućanstvo i kontakti su spremljeni. Dovrši slanje privole. Već poslane
          stranice ostaju sačuvane.
        </p>
      )}
      {duplikat && !spremljeni && (
        <div className="obavijest uvoz-upozorenje">
          <strong>Ova adresa već postoji: {duplikat.adresa}</strong>
          <p>
            {duplikat.nositelj_ime} {duplikat.nositelj_prezime}. Novo kućanstvo
            na istoj adresi ne može se ponovno stvoriti.
          </p>
          <button
            className="gumb sek"
            type="button"
            disabled={radi}
            onClick={() => naPostojece(duplikat.id)}
          >
            Otvori postojeće kućanstvo <Ikona naziv="strelica" velicina={16} />
          </button>
        </div>
      )}
      <PoljaKucanstva
        podaci={podaci}
        kontakti={kontakti}
        naPodatke={(v) => postaviNacrt({ ...nacrt, podaci: v })}
        naKontakte={(v) => postaviNacrt({ ...nacrt, kontakti: v })}
        zauzeto={radi || !!spremljeni}
        mozePotvrditi={jeUprava}
      />
      <section className="ploca privola-unosa">
        <div className="naslov-dokumenata">
          <span className="ikona-ploha tirkiz">
            <Ikona naziv="lokot" />
          </span>
          <div>
            <h2>GDPR privola</h2>
            <p>
              Priloži potpisanu privolu sada ili naknadno kroz dokumente
              kućanstva.
            </p>
          </div>
        </div>
        <OdabirDokumenata
          datoteke={privole}
          naPromjenu={postaviPrivole}
          zauzeto={radi || (!!spremljeni && !!privole.length)}
          naPripremu={postaviPripremaPrivole}
        />
        <label>
          Datum potpisivanja (ako je poznat)
          <input
            type="date"
            value={nacrt.datumPrivole || ''}
            disabled={radi || !!spremljeni}
            max={new Intl.DateTimeFormat('sv-SE', {
              timeZone: 'Europe/Zagreb',
            }).format(new Date())}
            onChange={(e) =>
              postaviNacrt({ ...nacrt, datumPrivole: e.target.value })
            }
          />
        </label>
        <p className="pomoc-dokumenata">
          Sken se sprema privatno uz kućanstvo. Prilaganje dokumenta ne
          potvrđuje automatski njegov sadržaj ili valjanost privole.
        </p>
      </section>
      {!!slicni.length && (
        <section className="ploca provjera-slicnosti">
          <h3>Moguće slična kućanstva</h3>
          <p>
            Isti osnovni broj, prezime ili lokacija unutar 15 m. To su
            prijedlozi za provjeru, ne automatsko povezivanje.
          </p>
          {slicni.map((k) => (
            <div key={k.id}>
              {k.adresa} · {k.nositelj_ime} {k.nositelj_prezime}
            </div>
          ))}
          <label className="redak-kvacice">
            <input
              type="checkbox"
              checked={slicnoPotvrdjeno}
              disabled={radi}
              onChange={(e) => postaviSlicnoPotvrdjeno(e.target.checked)}
            />
            Provjerio/la sam da je ovo zasebno kućanstvo.
          </label>
        </section>
      )}
      {izvan && (
        <label className="redak-kvacice obavijest uvoz-upozorenje">
          <input
            type="checkbox"
            checked={izvanPotvrdjeno}
            onChange={(e) => postaviIzvanPotvrdjeno(e.target.checked)}
            disabled={radi}
          />
          Koordinate su izvan područja vodovoda. Potvrđujem da su ispravne.
        </label>
      )}
      {!imaVezu && (
        <p className="obavijest uvoz-upozorenje" role="status">
          Nema internetske veze.{' '}
          {urediId
            ? 'Ostavi obrazac otvoren do povratka veze.'
            : 'Nacrt ostaje u ovoj kartici do odjave ili zatvaranja.'}{' '}
          Spremanje u bazu moguće je nakon povratka veze.
        </p>
      )}
      <div className="radnje-obrasca">
        <button
          className="gumb sek"
          type="button"
          onClick={naOdustani}
          disabled={radi}
        >
          {urediId ? 'Odustani od izmjena' : 'Vrati se na popis'}
        </button>
        <button
          className="gumb"
          type="submit"
          disabled={
            radi ||
            pripremaPrivole ||
            (!spremljeni && (!!duplikat || ucitavaEvidenciju)) ||
            !imaVezu
          }
        >
          {napredakPrivole ||
            (radi
              ? 'Spremam…'
              : spremljeni
                ? 'Ponovi slanje privole'
                : urediId
                  ? 'Spremi izmjene'
                  : privole.length
                    ? 'Spremi kućanstvo i privolu'
                    : 'Spremi kućanstvo')}
          <Ikona naziv="potvrda" velicina={18} />
        </button>
      </div>
      {spremljeni && !radi && (
        <button
          type="button"
          className="tekst-gumb"
          onClick={() => {
            try {
              sessionStorage.removeItem(kljuc);
            } catch {
              /* Nacrt nije dostupan. */
            }
            naSpremljeno(spremljeni);
          }}
        >
          Dovrši unos bez preostalih priloga
        </button>
      )}
    </form>
  );
}
